import React, {PropsWithChildren, useEffect, useRef, useState} from 'react';
import './popover.scss';

interface PopoverProps {
    className?: string;
    children: JSX.Element;
    content: React.ReactNode;
}

function Popover({className, children, content}: PropsWithChildren<PopoverProps>): JSX.Element {
    const [isOpen, setIsOpen] = useState(false);
    const [animationState, setAnimationState] = React.useState('init');

    const menuRef = useRef<HTMLDivElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        let timer: NodeJS.Timeout;
        if (isOpen) {
            setAnimationState('entering');
        } else if (animationState !== 'init') {
            setAnimationState('exiting');
            timer = setTimeout(() => {
                setAnimationState('');
            }, 200);
        }
        return () => clearTimeout(timer);
    }, [isOpen, animationState]);

    useEffect(() => {
        const listener = (event: Event | TouchEvent): void => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node) && !wrapperRef.current?.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', listener);
        document.addEventListener('touchstart', listener);
        window.addEventListener('scroll', listener, true);

        return () => {
            document.removeEventListener('mousedown', listener);
            document.removeEventListener('touchstart', listener);
            window.removeEventListener('scroll', listener, true);
        };
    }, [menuRef]);

    return (
        <div ref={wrapperRef} className={`c-popover ${isOpen ? 'c-popover--open' : ''} ${className}`} data-cy="popover">
            <div ref={buttonRef} className="c-popover__button" onClick={() => setIsOpen(!isOpen)}>
                {children}
            </div>
            <div className={`c-popover__content c-popover__content--${animationState}`} ref={menuRef}>
                {content}
            </div>
        </div>
    );
}

export default Popover;
